import { DEFAULT_SURFACED_IN } from './defineFeature.js';
import { FeatureType } from './types.js';
function surfacedIn(def) {
    return def.surfacedIn ?? DEFAULT_SURFACED_IN;
}
/** Type guards over the FeatureDef union, so call sites narrow without `as` casts. */
export const isBooleanDef = (d) => d.type === FeatureType.Boolean;
export const isStringDef = (d) => d.type === FeatureType.String;
export const isNumberDef = (d) => d.type === FeatureType.Number;
export const isMarkdownDef = (d) => d.type === FeatureType.Markdown;
export const isMultiSelectDef = (d) => d.type === FeatureType.MultiSelect;
export const isJsonDef = (d) => d.type === FeatureType.Json;
export const isStringListDef = (d) => d.type === FeatureType.StringList;
/** Look up a multiselect option by propName + value. Returns undefined if the feature isn't a multiselect or the value isn't a known option. */
export function getOption(defs, propName, value) {
    const def = defs.find((d) => d.propName === propName);
    if (!def || !isMultiSelectDef(def))
        return undefined;
    const options = def.options;
    return options[value];
}
/**
 * True if the feature applies to the given area. Hides the `'all'` vs `[]`
 * wildcard duality from callers, and treats `surfacedIn` as implying
 * applicability so a feature defined at one scope but overridable at another
 * (e.g. agent-defined, brand-overridable) is still visible to the rollup
 * for the override scope.
 */
export function isApplicableTo(def, area) {
    if (def.areas === 'all' || def.areas.includes(area))
        return true;
    return surfacedIn(def).includes(area);
}
/** True if the feature is surfaced in the given management UI scope-tab. See {@link FeatureDefBase.surfacedIn}. */
export function isSurfacedIn(def, area) {
    return surfacedIn(def).includes(area);
}
/** True if the applicability metadata allows the given provider/category context. Absent metadata defaults to true. */
export function isApplicableForContext(applicability, context) {
    if (!applicability) {
        return true;
    }
    const providers = applicability.providers;
    if (providers && providers.length > 0) {
        if (!context?.provider || !providers.includes(context.provider)) {
            return false;
        }
    }
    const categories = applicability.categories;
    if (categories && categories.length > 0) {
        if (!context?.category || !categories.includes(context.category)) {
            return false;
        }
    }
    return true;
}
/** {@link isApplicableForContext} taking a FeatureDef directly. */
export function isFeatureApplicableForContext(def, context) {
    return isApplicableForContext(def.applicability, context);
}
/** Normalize def.areas to the storage shape: `'all'` becomes `[]`, matching the Features.areas "empty means all" convention. */
export function areasForStorage(def) {
    return def.areas === 'all' ? [] : [...def.areas];
}
